export type ConsentStatus = "accepted_all" | "rejected_all" | "custom";

export interface ConsentPreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentState {
  status: ConsentStatus;
  preferences: ConsentPreferences;
  updatedAt: string;
}

export const CONSENT_CHANGE_EVENT = "thinkeat:consent-changed";

const CONSENT_STORAGE_KEY = "thinkeat_cookie_consent";

const VALID_STATUSES: ConsentStatus[] = ["accepted_all", "rejected_all", "custom"];

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

function isBrowserEnvironment(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizePreferences(value: unknown): ConsentPreferences {
  const candidate = (value as Partial<ConsentPreferences> | null) ?? {};

  return {
    necessary: true,
    analytics: Boolean(candidate.analytics),
    marketing: Boolean(candidate.marketing),
  };
}

function isValidStatus(status: unknown): status is ConsentStatus {
  return typeof status === "string" && VALID_STATUSES.includes(status as ConsentStatus);
}

function clearInvalidConsent(): void {
  if (!isBrowserEnvironment()) return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

export function getConsent(): ConsentState | null {
  if (!isBrowserEnvironment()) return null;

  try {
    // Inside the try: `getItem` itself throws when storage is blocked (Safari
    // private mode, embedded contexts). Treating that as "no consent given" is
    // correct, and it must never take a screen down with it.
    const rawValue = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!rawValue) return null;

    const parsedValue = JSON.parse(rawValue) as Partial<ConsentState> | null;

    if (!parsedValue || !isValidStatus(parsedValue.status)) {
      clearInvalidConsent();
      return null;
    }

    return {
      status: parsedValue.status,
      preferences: normalizePreferences(parsedValue.preferences),
      updatedAt: typeof parsedValue.updatedAt === "string" ? parsedValue.updatedAt : new Date().toISOString(),
    };
  } catch {
    clearInvalidConsent();
    return null;
  }
}

export function setConsent(input: Pick<ConsentState, "status" | "preferences">): ConsentState {
  const consent: ConsentState = {
    status: input.status,
    preferences: normalizePreferences(input.preferences),
    updatedAt: new Date().toISOString(),
  };

  if (isBrowserEnvironment()) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    } catch (error) {
      // Same storage-blocked environments `getConsent` is hardened for. Letting
      // this throw leaves the cookie banner stuck open with nothing logged,
      // because it runs inside a click handler.
      console.error("Could not persist cookie consent:", error);
    }

    // Dispatched even when the write failed. Listeners re-read storage through
    // `canLoadAnalytics`, so an unpersisted "accept" reads back as no consent
    // and nothing loads. That is the correct direction to fail in.
    window.dispatchEvent(
      new CustomEvent(CONSENT_CHANGE_EVENT, {
        detail: consent,
      }),
    );
  }

  return consent;
}

export function canLoadAnalytics(): boolean {
  return getConsent()?.preferences.analytics === true;
}

export function canLoadMarketing(): boolean {
  return getConsent()?.preferences.marketing === true;
}

export function hasConsentDecision(): boolean {
  return getConsent() !== null;
}

export function getDefaultConsentPreferences(): ConsentPreferences {
  return { ...DEFAULT_PREFERENCES };
}
